import {
  type Call,
  type TranscriptSegment,
  useCalls,
  useCallTranscript,
} from '@hostesa/api-client';
import { useMemo } from 'react';
import { isCallActive } from '../../routes/calls/components/call-presentation.ts';
import type { LiveCallTurnVM, LiveCallVM, PresentationResult } from './types.ts';

/**
 * The reception right-rail live call. Real, not fixture-backed: the most recent
 * ACTIVE call from the canonical `useCalls` list, with its transcript streamed
 * over the realtime cache (the consumer mounts the call-room subscription). When
 * no call is on the line the envelope's `data` is `undefined` so the panel shows
 * its calm empty state. Speaker labels are applied by the page from i18n.
 */

/** Canonical transcript speaker → live-panel side (guest vs the assistant). */
function turnSpeaker(speaker: TranscriptSegment['speaker']): 'guest' | 'hostesa' {
  return speaker === 'guest' ? 'guest' : 'hostesa';
}

/** Upper-cased language code for a turn pill, or undefined when absent. */
function turnLang(language: unknown): string | undefined {
  return typeof language === 'string' && language.trim()
    ? language.trim().toUpperCase()
    : undefined;
}

/** The most recently started active call, or null when the desk is quiet. */
function mostRecentActive(calls: readonly Call[]): Call | null {
  const at = (c: Call) => new Date(c.answeredAt ?? c.startedAt ?? c.createdAt).getTime();
  return calls.filter((c) => isCallActive(c.status)).sort((a, b) => at(b) - at(a))[0] ?? null;
}

export function useLiveCall(tenantId: string, hotelId: string): PresentationResult<LiveCallVM> {
  const calls = useCalls(tenantId, hotelId, { limit: 50 });
  const active = useMemo(() => mostRecentActive(calls.data?.items ?? []), [calls.data]);
  // `useCallTranscript` self-disables on an empty id, so a quiet desk makes no request.
  const transcript = useCallTranscript(tenantId, hotelId, active?.id ?? '');

  const data = useMemo<LiveCallVM | undefined>(() => {
    if (!active) return undefined;
    const turns: LiveCallTurnVM[] = [...(transcript.data ?? [])]
      .sort((a, b) => a.seq - b.seq)
      .map((seg) => ({
        id: seg.id,
        speaker: turnSpeaker(seg.speaker),
        lang: turnLang(seg.language),
        text: seg.text,
      }));
    return { callId: active.id, direction: active.direction, language: active.language, turns };
  }, [active, transcript.data]);

  return { data, isLoading: calls.isLoading, isError: calls.isError };
}
